import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrinterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { App, Button, Card, Flex, Grid, Tabs, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import SupplierLedgerFilters from "./components/SupplierLedgerFilters";
import SupplierLedgerTable from "./components/SupplierLedgerTable";
import SupplierLedgerPrintView from "./components/SupplierLedgerPrintView";
import ReportDom from "../../../components/common/ReportDom";

const { Title } = Typography;

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const SupplierLedger = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { message } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens   = useBreakpoint();
  const reportRef = useRef(null);

  const {
    list,
    filters,
    linkedParty,
    closingBalance,
    customerClosingBalance,
    customerLedgerList,
    customerOpeningBalance,
  } = useSelector((state) => state.supplierLedger);

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Supplier Ledger Report",
    removeAfterPrint: true,
  });

  // positive = they owe you more than you owe them
  const netBalance = customerClosingBalance - closingBalance;

  return (
    <div className="p-4">
      <App>
        {/* ── Header ─────────────────────────────────────────── */}
        <Card
          className="shadow-md rounded-xl mb-4"
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/reports")}
                style={{ marginRight: 8 }}
              />
              <Title level={4} style={{ margin: 0 }}>Supplier Ledger</Title>
            </div>
          }
          extra={
            list.length > 0 && (
              <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
                {!screens.xs && "Print"}
              </Button>
            )
          }
          styles={{
            header: { borderBottom: "none", padding: "12px 16px" },
            body:   { padding: 0 },
          }}
        >
          <SupplierLedgerFilters messageApi={message} />
        </Card>

        {/* ── Net balance summary — only when linked customer exists ── */}
        {linkedParty && list.length > 0 && (
          <Card
            className="shadow-md rounded-xl mb-4"
            style={{ background: "#f0f5ff", borderColor: "#adc6ff" }}
            bodyStyle={{ padding: "16px 24px" }}
          >
            <Flex gap={40} wrap="wrap" align="center">
              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>Supplier Balance</div>
                <div style={{ fontSize: 11, color: "#ff4d4f", marginBottom: 4 }}>You owe them</div>
                <div style={{ fontWeight: 700, fontSize: 20, color: "#ff4d4f" }}>
                  {fmt(closingBalance)}
                </div>
              </div>

              <div style={{ fontSize: 20, color: "#ccc" }}>—</div>

              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>Customer Balance</div>
                <div style={{ fontSize: 11, color: "#1890ff", marginBottom: 4 }}>They owe you</div>
                <div style={{ fontWeight: 700, fontSize: 20, color: "#1890ff" }}>
                  {fmt(customerClosingBalance)}
                </div>
              </div>

              <div style={{ fontSize: 20, color: "#ccc" }}>=</div>

              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>Net Balance</div>
                <div style={{
                  fontSize: 11,
                  color: netBalance >= 0 ? "#52c41a" : "#ff4d4f",
                  marginBottom: 4,
                }}>
                  {netBalance >= 0 ? "In your favor" : "You owe them"}
                </div>
                <div style={{
                  fontWeight: 700,
                  fontSize: 20,
                  color: netBalance >= 0 ? "#52c41a" : "#ff4d4f",
                }}>
                  {fmt(Math.abs(netBalance))}
                </div>
              </div>

              <div style={{ marginLeft: "auto", fontSize: 12, color: "#888" }}>
                Customer A/C: <strong>{linkedParty.linked_acc_code}</strong>
              </div>
            </Flex>
          </Card>
        )}

        {/* ── Ledger tabs or single card ──────────────────────── */}
        {linkedParty && list.length > 0 ? (
          <Card className="shadow-md rounded-xl">
            <Tabs
              defaultActiveKey="supplier"
              size="middle"
              items={[
                {
                  key:   "supplier",
                  label: (
                    <span>
                      Supplier Ledger
                      <span style={{ marginLeft: 8, fontSize: 12, color: "#ff4d4f", fontWeight: 600 }}>
                        {fmt(closingBalance)}
                      </span>
                    </span>
                  ),
                  children: <SupplierLedgerTable />,
                },
                {
                  key:   "customer",
                  label: (
                    <span>
                      Customer Ledger
                      <span style={{ marginLeft: 8, fontSize: 12, color: "#1890ff", fontWeight: 600 }}>
                        {fmt(customerClosingBalance)}
                      </span>
                    </span>
                  ),
                  children: (
                    <SupplierLedgerTable
                      isCustomerTab
                      customerList={customerLedgerList}
                      customerOpening={customerOpeningBalance}
                      customerClosing={customerClosingBalance}
                    />
                  ),
                },
              ]}
            />
          </Card>
        ) : (
          <Card className="shadow-md rounded-xl">
            <SupplierLedgerTable />
          </Card>
        )}
      </App>

      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Supplier Ledger Report"
          mode="report-page"
          filters={filters}
          renderTable={() => <SupplierLedgerPrintView />}
        />
      </div>
    </div>
  );
};

export default SupplierLedger;